import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import logoColor from "../assets/logo-color-blanco.svg";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import ScheduleCallForm from "./ScheduleCallForm";
import UserMenu from "./UserMenu";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [location] = useLocation();
  const [isNosotrosDropdownOpen, setIsNosotrosDropdownOpen] = useState(false);
  const [isMobileNosotrosOpen, setIsMobileNosotrosOpen] = useState(false);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const nosotrosSubRoutes = ["/equipo", "/piensa-en-verde", "/servicios/b2b", "/servicios/plan-renove"];
  const isNosotrosActive = nosotrosSubRoutes.some(route => location.startsWith(route));

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    console.error("Error loading logo:", e);
    const img = e.target as HTMLImageElement;
    console.log("Attempted image path:", img.src);
  };

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 px-4 md:px-6
                ${
                  isScrolled
                    ? "bg-[#080809] bg-carbon-fiber backdrop-blur-lg shadow-lg border-b border-white/10 py-3 md:py-4"
                    : "bg-transparent py-4 md:py-6"
                }`}
      style={{ zIndex: 100 }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-12">
          <a
            href="/"
            className="logo-container relative cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = "/";
            }}
          >
            <img
              src={logoColor}
              alt="InnoTec Food"
              className={`transition-all duration-300 ${
                isScrolled ? "h-6 md:h-8" : "h-8 md:h-12"
              }`}
              onError={handleImageError}
            />
          </a>
          <div className="hidden md:flex items-center space-x-8">
            <a
              href="/new"
              className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium relative group cursor-pointer
                        px-3 py-2 rounded-md transition-all duration-300 ease-out
                        ${location === "/new"
                          ? "text-white bg-white/5 border border-white/10"
                          : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            >
              Maquinaria Innotec
              <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#FCAA0D] transform origin-left
                             transition-transform duration-300 ease-out
                             ${location === "/new" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
            </a>
            <a
              href="/used"
              className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium relative group cursor-pointer
                        px-3 py-2 rounded-md transition-all duration-300 ease-out
                        ${location === "/used"
                          ? "text-white bg-white/5 border border-white/10"
                          : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            >
              Ocasión
              <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#FCAA0D] transform origin-left
                             transition-transform duration-300 ease-out
                             ${location === "/used" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
            </a>
            <a
              href="/catalogos"
              className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium relative group cursor-pointer
                        px-3 py-2 rounded-md transition-all duration-300 ease-out
                        ${location === "/catalogos"
                          ? "text-white bg-white/5 border border-white/10"
                          : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            >
              Catálogos
              <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#FCAA0D] transform origin-left
                             transition-transform duration-300 ease-out
                             ${location === "/catalogos" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
            </a>
            <div
              className="relative"
              onMouseEnter={() => {
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current);
                  leaveTimeoutRef.current = null;
                }
                setIsNosotrosDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (leaveTimeoutRef.current) {
                  clearTimeout(leaveTimeoutRef.current);
                }
                leaveTimeoutRef.current = setTimeout(() => {
                  setIsNosotrosDropdownOpen(false);
                }, 300);
              }}
            >
              <button
                type="button"
                onClick={() => setIsNosotrosDropdownOpen(prev => !prev)}
                className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium relative group cursor-pointer
                          px-3 py-2 rounded-md transition-all duration-300 ease-out flex items-center
                          ${isNosotrosActive
                            ? "text-white bg-white/5 border border-white/10"
                            : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
              >
                Nosotros
                <svg className={`w-4 h-4 ml-1.5 transition-transform duration-200 transform ${isNosotrosDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#FCAA0D] transform origin-left
                               transition-transform duration-300 ease-out
                               ${isNosotrosActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
              </button>
              {isNosotrosDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-56 bg-[#0c0c0d] border border-white/10 rounded-md shadow-xl py-2 z-[101] backdrop-blur-md"
                >
                  <a href="/equipo" onClick={() => setIsNosotrosDropdownOpen(false)} className={`block px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-out ${location === "/equipo" ? "text-[#FCAA0D] bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`}>Equipo</a>
                  <a href="/piensa-en-verde" onClick={() => setIsNosotrosDropdownOpen(false)} className={`block px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-out ${location === "/piensa-en-verde" ? "text-[#FCAA0D] bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`}>Piensa en Verde</a>
                  <a href="/servicios/b2b" onClick={() => setIsNosotrosDropdownOpen(false)} className={`block px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-out ${location === "/servicios/b2b" ? "text-[#FCAA0D] bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`}>Servicio B2B</a>
                  <a href="/servicios/plan-renove" onClick={() => setIsNosotrosDropdownOpen(false)} className={`block px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-out ${location === "/servicios/plan-renove" ? "text-[#FCAA0D] bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`}>Plan Renove</a>
                </div>
              )}
            </div>
            <a
              href="/contacto"
              className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium relative group cursor-pointer
                        px-3 py-2 rounded-md transition-all duration-300 ease-out
                        ${location === "/contacto"
                          ? "text-white bg-white/5 border border-white/10"
                          : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            >
              Contacto
              <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#FCAA0D] transform origin-left
                             transition-transform duration-300 ease-out
                             ${location === "/contacto" ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
            </a>
          </div>
        </div>

        <div className="hidden md:flex items-center space-x-4">
          <Button
            className="font-montserrat uppercase tracking-wide text-sm font-bold
                      bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 backdrop-blur-sm
                      border border-white/30 hover:border-white/50 shadow-lg
                      text-black transition-all duration-300 ease-out
                      hover:shadow-[#FCAA0D]/20 hover:shadow-xl
                      hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => setIsCallModalOpen(true)}
          >
            Agendar una llamada
          </Button>
          <UserMenu />
        </div>

        <button
          className={`md:hidden p-2 -mr-2 transition-transform duration-300 ${isMenuOpen ? "active" : ""}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <div className="flex flex-col gap-1.5">
            <div className={`hamburger-line h-0.5 w-6 bg-white transition-all duration-300 ${
              isMenuOpen ? "rotate-45 translate-y-2" : ""
            }`}></div>
            <div className={`hamburger-line h-0.5 w-6 bg-white transition-all duration-300 ${
              isMenuOpen ? "opacity-0" : ""
            }`}></div>
            <div className={`hamburger-line h-0.5 w-6 bg-white transition-all duration-300 ${
              isMenuOpen ? "-rotate-45 -translate-y-2" : ""
            }`}></div>
          </div>
        </button>
      </div>

      {/* Línea luminosa naranja */}
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[#FCAA0D]/50
                    after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px]
                    after:bg-[#FCAA0D] after:blur-[1px]
                    before:absolute before:bottom-0 before:left-0 before:w-full before:h-[1px]
                    before:bg-[#FCAA0D]/20 before:blur-[4px]
                    transition-opacity duration-500 ease-out"
           style={{
             opacity: isScrolled ? 1 : 0.3,
             boxShadow: `0 0 10px 0 rgba(252, 170, 13, ${isScrolled ? 0.3 : 0.1}),
                        0 0 20px 0 rgba(252, 170, 13, ${isScrolled ? 0.2 : 0.05})`,
           }}
      />

      <div
        className={`md:hidden fixed inset-x-0 top-[var(--navbar-height)]
                   bg-[#0A0A0B]/95 backdrop-blur-xl border-b border-white/5
                   shadow-lg transition-all duration-300 ease-out
                   ${isMenuOpen
                     ? "opacity-100 translate-y-0"
                     : "opacity-0 -translate-y-4 pointer-events-none"}`}
        style={{
          '--navbar-height': isScrolled ? '3.5rem' : '4.5rem',
        } as React.CSSProperties}
      >
        <div className="px-4 py-4 flex flex-col space-y-4">
          <a
            href="/new"
            className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium py-2
                     px-3 rounded-md transition-all duration-300 relative group cursor-pointer
                     ${location === "/new"
                       ? "text-white bg-white/5 border border-white/10"
                       : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Maquinaria Nueva
            <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FCAA0D]
                         transition-all duration-300 group-hover:w-full`} />
          </a>
          <a
            href="/used"
            className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium py-2
                     px-3 rounded-md transition-all duration-300 relative group cursor-pointer
                     ${location === "/used"
                       ? "text-white bg-white/5 border border-white/10"
                       : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Ocasión
            <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FCAA0D]
                         transition-all duration-300 group-hover:w-full`} />
          </a>
          <a
            href="/catalogos"
            className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium py-2
                     px-3 rounded-md transition-all duration-300 relative group cursor-pointer
                     ${location === "/catalogos"
                       ? "text-white bg-white/5 border border-white/10"
                       : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Catálogos
            <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FCAA0D]
                         transition-all duration-300 group-hover:w-full`} />
          </a>
          <div>
            <button
              type="button"
              className={`w-full text-left nav-link font-montserrat uppercase tracking-wide text-sm font-medium py-2
                       px-3 rounded-md transition-all duration-300 relative group cursor-pointer flex justify-between items-center
                       ${isNosotrosActive
                         ? "text-white bg-white/5 border border-white/10"
                         : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
              onClick={() => { setIsMobileNosotrosOpen(prev => !prev); }}
            >
              Nosotros
              <svg className={`w-4 h-4 transition-transform duration-200 ${isMobileNosotrosOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </button>
            {isMobileNosotrosOpen && (
              <div className="pt-2 pb-1 pl-4 flex flex-col space-y-1 border-l-2 border-white/10 ml-3 mt-1">
                <a
                  href="/equipo"
                  className={`block py-1.5 px-2 rounded-md font-montserrat uppercase tracking-tight text-xs font-medium transition-all duration-200 ease-out ${
                    location === "/equipo" ? "text-[#FCAA0D] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                  onClick={() => { setIsMenuOpen(false); setIsMobileNosotrosOpen(false); }}
                >
                  Equipo
                </a>
                <a
                  href="/piensa-en-verde"
                  className={`block py-1.5 px-2 rounded-md font-montserrat uppercase tracking-tight text-xs font-medium transition-all duration-200 ease-out ${
                    location === "/piensa-en-verde" ? "text-[#FCAA0D] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                  onClick={() => { setIsMenuOpen(false); setIsMobileNosotrosOpen(false); }}
                >
                  Piensa en Verde
                </a>
                <a
                  href="/servicios/b2b"
                  className={`block py-1.5 px-2 rounded-md font-montserrat uppercase tracking-tight text-xs font-medium transition-all duration-200 ease-out ${
                    location === "/servicios/b2b" ? "text-[#FCAA0D] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                  onClick={() => { setIsMenuOpen(false); setIsMobileNosotrosOpen(false); }}
                >
                  Servicio B2B
                </a>
                <a
                  href="/servicios/plan-renove"
                  className={`block py-1.5 px-2 rounded-md font-montserrat uppercase tracking-tight text-xs font-medium transition-all duration-200 ease-out ${
                    location === "/servicios/plan-renove" ? "text-[#FCAA0D] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                  onClick={() => { setIsMenuOpen(false); setIsMobileNosotrosOpen(false); }}
                >
                  Plan Renove
                </a>
              </div>
            )}
          </div>
          <a
            href="/contacto"
            className={`nav-link font-montserrat uppercase tracking-wide text-sm font-medium py-2
                     px-3 rounded-md transition-all duration-300 relative group cursor-pointer
                     ${location === "/contacto"
                       ? "text-white bg-white/5 border border-white/10"
                       : "text-white/70 hover:text-white hover:bg-white/5 hover:border hover:border-white/10"}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Contacto
            <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FCAA0D]
                         transition-all duration-300 group-hover:w-full`} />
          </a>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              className="w-full font-montserrat uppercase tracking-wide text-sm font-bold
                        bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 backdrop-blur-sm
                        border border-white/30 hover:border-white/50 shadow-lg
                        text-black transition-all duration-300 ease-out
                        hover:shadow-[#FCAA0D]/20 hover:shadow-xl
                        hover:scale-[1.02] active:scale-[0.98]"
              onClick={() => {
                setIsMenuOpen(false);
                setIsCallModalOpen(true);
              }}
            >
              Agendar una llamada
            </Button>
            <div className="w-full">
              <UserMenu className="w-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="relative" style={{ zIndex: 9999 }}>
        <Dialog open={isCallModalOpen} onOpenChange={setIsCallModalOpen}>
          <DialogContent className="bg-card/95 backdrop-blur-lg border border-border fixed" style={{ zIndex: 9999 }}>
            <ScheduleCallForm
              onSuccess={() => setIsCallModalOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </nav>
  );
}
